import React, { useEffect, useState, useCallback } from "react";
import { ProductFacetResult, StringProductDataValueFacetResult } from "@relewise/client";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { ChecklistFacet } from "../ChecklistFacet/ChecklistFacet.component";

export interface RelewiseFacetsProps {
    facets?: ProductFacetResult;
    update?: (items: unknown) => void;
}

export const RelewiseFacets = (props: RelewiseFacetsProps) => {
    const { getDictionaryString } = useTranslations();
    const { selectedFacets, setSelectedFacets, updateSearchParams, generateFacetQueryString } =
        useRelewise();
    const [hasFacets, setHasFacets] = useState<boolean>();

    useEffect(() => {
        const facetCount = props.facets?.items?.reduce(
            (total, item: StringProductDataValueFacetResult) => {
                // Only count items with non-empty values
                const validItems =
                    item.available?.filter(
                        (option) => option.value && option.value.trim() !== ""
                    ) || [];
                return total + validItems.length;
            },
            0
        );
        setHasFacets(facetCount > 0);
    }, [props.facets]);

    const getFacetsByType = (selectedFacets: Record<string, string[]>, type: string) => {
        if ((selectedFacets && !selectedFacets[type]) || selectedFacets[type].length < 1) {
            return null;
        }
        return selectedFacets[type];
    };

    const setFacet = useCallback(
        (type: string, value: string) => {
            const currentSelectFacetValues = getFacetsByType(selectedFacets, type);
            const valueAlreadySelected =
                (currentSelectFacetValues?.find((v) => v === value)?.length ?? 0) > 0;

            const newSelectFacets = { ...selectedFacets };
            if (valueAlreadySelected) {
                const indexToRemove = newSelectFacets[type].indexOf(value);
                newSelectFacets[type] = [
                    ...newSelectFacets[type].slice(0, indexToRemove),
                    ...newSelectFacets[type].slice(indexToRemove + 1),
                ];
            } else {
                newSelectFacets[type] = [...(newSelectFacets[type] || []), value];
            }
            setSelectedFacets(newSelectFacets);

            // Sync to URL using context helpers
            if (updateSearchParams && generateFacetQueryString) {
                updateSearchParams(generateFacetQueryString());
            }
        },
        [selectedFacets, setSelectedFacets, updateSearchParams, generateFacetQueryString]
    );

    return (
        <>
            {hasFacets ? (
                <DA_Title h3 noMargin>
                    {getDictionaryString("filters")}
                </DA_Title>
            ) : null}

            {props.facets?.items?.map((facet, index) => {
                const facetResult = facet as StringProductDataValueFacetResult;
                // Only show facets that have at least one non-empty value
                const hasValidValues = facetResult.available?.some(
                    (option) => option.value && option.value.trim() !== ""
                );

                return hasValidValues ? (
                    <div key={index}>
                        {facet.field === "Data" ? (
                            <ChecklistFacet
                                expanded={index < 3}
                                facet={facetResult}
                                selected={selectedFacets[facetResult.key]}
                                setFacet={setFacet}
                            />
                        ) : null}
                    </div>
                ) : null;
            })}
        </>
    );
};
